// French WhatsApp follow-up message templates — one per sub-state.
//
// Spec: docs/superpowers/specs/2026-06-04-smart-whatsapp-resume-design.md
//
// All copy is VERBATIM from the user's brief — do not change wording without
// rerunning the spec. Each function takes the substitution vars and returns
// the rendered message ready for encodeURIComponent + wa.me/<intl>?text=.
//
// Why factory functions and not template strings:
//   - Some sub-states only render the destination when it's known (album 1
//     destination is reliable; later-album destination might be empty for
//     dropped Duo/Trio orders).
//   - Lets us strip the trailing "— Lien: …" line because WhatsApp Web
//     auto-shortens any URL pasted at the end, so we keep the link as the
//     LAST line of every message for the best preview.

import type { SubState } from './followUpStateMachine';

export interface MessageVars {
  /** Customer's first name (already extracted; falls back to empty string). */
  firstName: string;
  /** Album 1 destination (or whichever album the link targets). May be empty. */
  destination: string;
  /** ALL album destinations for the order, comma-joined in album order
   *  ("Paris, Italie, Belgique"), used by the Album Soumis message which lists
   *  every album. Falls back to `destination` (single) when absent. */
  destinationsList?: string;
  /** Resume link — already URL-encoded? NO. The caller passes the raw URL;
   *  the template embeds it verbatim and the caller is responsible for
   *  wrapping the WHOLE message in encodeURIComponent. */
  link: string;
  /** Confirmation acompte in MAD for the `submitted` (Album Soumis) message =
   *  base advance × number of albums. Caller computes it via
   *  {@link computeAcompte}. Other sub-states ignore it. */
  acompte?: number;
  /** Full order total in MAD — shown on the deposit message's "Ou paiement
   *  complet" line so the customer can pay everything up front instead. */
  total?: number;
  /** Bank account for the payment relances (C1–C4). */
  rib?: string;
}

/** Helper — render the destination clause only when we have one. Keeps the
 *  copy clean for orders where the customer didn't fill the destination
 *  field on the COD form. */
function dest(v: MessageVars): string {
  return v.destination.trim() ? ` ${v.destination.trim()}` : '';
}

/** Bank details for the payment messages. Falls back to the account we have
 *  always used so an un-configured caller still sends something valid. */
function rib(v: MessageVars): string {
  return (v.rib ?? '').trim() || '230 330 3719980211005800 16';
}

/** Helper — name greeting with fallback. */
function name(v: MessageVars): string {
  return v.firstName.trim() || 'cher client';
}

// ─── The relance ladders (2026-08-12) ───────────────────────────────────────
// Texts written by Yahya. One ARRAY per state = the sequence of relances: the
// 1st is light, the last is firm. Before this there was ONE message per state,
// so a client who ignored it was simply never chased again — measured: 27 of 49
// live orders had never received a single follow-up, average 0.6 per order.
//
// Picked with `followUpMessage(subState, attempt, vars)`. Attempts past the end
// of a ladder reuse the last (firmest) message rather than wrapping around.
export const FOLLOW_UP_LADDERS: Record<SubState, ReadonlyArray<(v: MessageVars) => string>> = {
  // A — waiting on photos + title. J+1, J+3, J+7, J+14.
  photos_missing: [
    (v) => `Bonjour ${name(v)}, vous avez pu rassembler vos photos et le titre pour qu'on commence ?`,
    () => `Petit rappel pour l'envoi de vos photos, on a hâte de voir ça.`,
    (v) => `On met la commande en attente ${name(v)}. Prévenez-nous quand vous avez les photos.`,
    () => `Sans nouvelles de vous d'ici demain, on devra malheureusement annuler la commande pour libérer la place.`,
  ],

  // Same ladder: an order with no session at all is stuck for the same reason.
  not_started: [
    (v) => `Bonjour ${name(v)}, vous avez pu rassembler vos photos et le titre pour qu'on commence ?`,
    () => `Petit rappel pour l'envoi de vos photos, on a hâte de voir ça.`,
    (v) => `On met la commande en attente ${name(v)}. Prévenez-nous quand vous avez les photos.`,
    () => `Sans nouvelles de vous d'ici demain, on devra malheureusement annuler la commande pour libérer la place.`,
  ],

  // D3/D4 — cover catalogue link. ADMIN-TRIGGERED, never derived: the client
  // only owes us a cover once we have actually sent them the link.
  cover_not_chosen: [
    (v) => `Bonjour ${name(v)}, à vous de choisir la couverture de ${dest(v).trim() || 'votre album'}. Ça prend une minute, tout est sur ce catalogue : ${v.link}`,
    (v) => `Petit rappel ${name(v)}, on attend votre choix de couverture via le lien pour pouvoir avancer : ${v.link}`,
    () => `Il ne manque plus que votre choix de couverture pour lancer la fabrication de l'album. C'est juste ici : ${'{link}'}`,
  ],

  // D1/D2 — Duo/Trio, later album still empty. J+2, J+6.
  album_2_missing: [
    () => `Il nous manque juste les photos de votre deuxième album pour avancer.`,
    (v) => `Bonjour ${name(v)}, on est bloqués sur le deuxième album sans vos photos, vous les envoyez quand ?`,
  ],

  // B — preview sent, no answer. J+1, J+3, J+7.
  awaiting_preview_feedback: [
    (v) => `Bonjour ${name(v)}, vous avez pu jeter un œil à l'aperçu 3D sur ce lien ? ${v.link}`,
    (v) => `Dites-moi si le design vous plaît ou s'il faut ajuster des choses via ${v.link}.`,
    (v) => `C'est notre dernière relance ${name(v)}. On attend votre validation finale sur ${v.link} pour imprimer.`,
  ],

  // C — approved but unpaid. THE money leak. J+1, J+3, J+7, J+14.
  awaiting_payment: [
    (v) => `Bonjour ${name(v)}, on lance l'impression dès réception de l'avance de ${v.acompte ?? ''} MAD. Voici le RIB : ${rib(v)}`,
    (v) => `Plus vite le règlement de ${v.acompte ?? ''} MAD est fait, plus vite l'album part à l'impression. RIB : ${rib(v)}`,
    (v) => `Petit mot pour vous rappeler l'acompte de ${v.acompte ?? ''} MAD sur ce compte ${rib(v)} pour avancer.`,
    (v) => `Sans votre règlement de ${v.acompte ?? ''} MAD aujourd'hui, on met l'impression en pause définitive. RIB : ${rib(v)}`,
  ],

  // WAITING ON US (2026-09-29): submitted, nothing to ask her yet — no money,
  // no promise of a date. Harmless if a person sends it anyway.
  submitted_waiting: [
    (v) => `Bonjour ${name(v)}, merci ! Votre album est bien reçu, on s'en occupe et on revient vers vous très vite.`,
  ],

  // WAITING ON US — never sent to a client. Present so the Record is total.
  changes_requested: [
    (v) => `On a bien fait les modifications demandées. Dites-moi si c'est tout bon sur ce lien ${v.link}.`,
  ],

  editor_stuck: [
    (v) => `Bonjour ${name(v)}, votre album${dest(v)} est presque prêt — il reste juste à cliquer sur Terminer. ${v.link}`,
    (v) => `Un coup de main pour finir votre album ? Répondez-moi ici. ${v.link}`,
  ],

  // RESTORED VERBATIM (2026-08-12). This is the deposit ask, not a relance —
  // rewriting it dropped the account holder, the "Petite avance (minimum)"
  // wording and the multi-album destination list, all of which are asserted in
  // followUpMessages.test.ts because clients pay against this exact text.
  submitted: [
    (v) => `Bonjour ${name(v)} 😊\n\nVos albums ${(v.destinationsList ?? v.destination ?? '').trim()} sont bien reçus — merci pour votre confiance ! 🎉\n\nPour lancer l'impression, il nous faut une petite avance de votre part :\n\n🏦 Titulaire : OMAR IHAHANE\n💳 RIB : ${rib(v)}\n💰 Petite avance (minimum) : ${v.acompte ?? ''} MAD\n✨ Ou paiement complet : ${v.total ?? ''} MAD\n\nEnvoyez-nous la photo du reçu et on s'occupe de tout 🙏\n\nL'équipe Storiz`,
  ],

  printed_not_shipped: [
    (v) => `Bonjour ${name(v)}, votre album est imprimé. La livraison est organisée très prochainement.`,
  ],
  in_transit: [
    (v) => `Bonjour ${name(v)}, votre album est en route, vous le recevrez bientôt.`,
  ],
  delivered: [
    (v) => `Bonjour ${name(v)}, votre album est livré. On espère qu'il vous plaît — un avis nous ferait vraiment plaisir.`,
  ],
};

/** One-off messages (not relances — sent once, on an event). */
export const ONE_OFF_MESSAGES = {
  /** E1 — the 3D preview has just been generated. */
  preview_ready: (v: MessageVars) =>
    `Le design de ${dest(v).trim() || 'votre album'} est prêt. Vous pouvez le feuilleter et le valider ici : ${v.link} ✨`,
  /** E2 — we finished the changes the client asked for. */
  changes_done: (v: MessageVars) =>
    `On a bien fait les modifications demandées. Dites-moi si c'est tout bon sur ce lien ${v.link}.`,
  /** E3 — payment landed. */
  payment_received: (v: MessageVars) =>
    `Paiement bien reçu, merci. L'impression de ${dest(v).trim() || 'votre album'} est lancée.`,
  /** E4 — first send of the cover catalogue link. */
  cover_link: (v: MessageVars) =>
    `Bonjour ${name(v)}, à vous de choisir la couverture de ${dest(v).trim() || 'votre album'}. Ça prend une minute, tout est sur ce catalogue : ${v.link}`,
} as const;

// F1/F2 — two upsell texts stood here, each quoting a flat 79 dh for the 3D
// cover finish: a figure a Duo is never charged, and nothing ever sent them.
// Removed 2026-09-29 (owner, « 1a »). The WhatsApp agent offers that option at
// the order's pack price (worker lib/reliefPricing.ts).

/**
 * The message for this state at this attempt (1-based).
 *
 * Attempt 5 of a 4-message ladder returns the 4th — we escalate to the firmest
 * wording and stay there rather than looping back to the friendly opener.
 */
export function followUpMessage(
  subState: SubState,
  attempt: number,
  vars: MessageVars,
): string {
  const ladder = FOLLOW_UP_LADDERS[subState];
  const i = Math.min(Math.max(1, Math.round(attempt || 1)), ladder.length) - 1;
  return ladder[i]!(vars);
}

/** How many relances this state has before it repeats its last message. */
export function ladderLength(subState: SubState): number {
  return FOLLOW_UP_LADDERS[subState].length;
}

/** Backwards-compatible single-message view = the FIRST relance of each state. */
export const FOLLOW_UP_MESSAGES: Record<SubState, (vars: MessageVars) => string> =
  Object.fromEntries(
    (Object.keys(FOLLOW_UP_LADDERS) as SubState[]).map((k) => [k, FOLLOW_UP_LADDERS[k][0]!]),
  ) as Record<SubState, (vars: MessageVars) => string>;

/** Sub-states that have a link (admin UI should show the message but no
 *  "Envoyer" button if the order is past the funnel — those are
 *  informational nudges, not recovery prompts). */
export const SUB_STATES_WITH_LINK: ReadonlySet<SubState> = new Set<SubState>([
  'not_started',
  'photos_missing',
  'cover_not_chosen',
  'album_2_missing',
  'editor_stuck',
  'awaiting_preview_feedback',
]);

/** Sub-states whose message can be SENT from the bulk page. Superset of
 *  SUB_STATES_WITH_LINK plus `submitted`: the Album Soumis deposit message has
 *  no resume link but is still actionable (admin chases the confirmation
 *  payment). The link line in the UI stays gated on SUB_STATES_WITH_LINK. */
export const SUB_STATES_SENDABLE: ReadonlySet<SubState> = new Set<SubState>([
  ...SUB_STATES_WITH_LINK,
  'submitted',
]);

/** Confirmation acompte for the Album Soumis deposit message = the base
 *  advance (app_settings.advance_amount, default 50) × the number of albums in
 *  the order. So Solo→50, Duo→100, Trio→150. ⚠️ NOT the AMEEX COD deduction
 *  any more (money bug 3, 2026-09-29): the courier collects total − what was
 *  actually received, see lib/cashToCollect.ts.
 *  Decided with Yahya: always 50×albums (the deposit message also shows the
 *  full total on its own line, so there is no longer a virement-vs-acompte
 *  branch here). Album count comes from `albumCountForBundle(order.bundle)`. */
export function computeAcompte(baseAdvance: number, albumCount: number): number {
  const base = Math.max(0, Math.round(Number(baseAdvance) || 0));
  const n = Math.max(1, Math.round(Number(albumCount) || 1));
  return base * n;
}

// ─── Editable template strings ─────────────────────────────────────────────
// These are the same text as FOLLOW_UP_MESSAGES above, but expressed as raw
// strings with `{firstName}`, `{destination}`, `{link}` placeholders so admin
// can edit them in the UI and save to app_settings.follow_up_templates.
//
// Why duplicate the copy: the factory functions in FOLLOW_UP_MESSAGES contain
// branching for `destination` falling back to nothing, which is not easy to
// express in a plain-text template. The templates here pick the destination-
// always-present form because empty destination just renders as an extra
// space, which is fine for editing UX. If admin DOES customize, the empty-
// destination edge case is acceptable.
export const DEFAULT_TEMPLATES: Record<SubState, string> = {
  not_started:
    `Bonjour {firstName}, vous avez pu rassembler vos photos et le titre pour qu'on commence ?`,
  photos_missing:
    `Bonjour {firstName}, vous avez pu rassembler vos photos et le titre pour qu'on commence ?`,
  cover_not_chosen:
    `Bonjour {firstName}, à vous de choisir la couverture de {destination}. Ça prend une minute, tout est sur ce catalogue : {link}`,
  album_2_missing:
    `Il nous manque juste les photos de votre deuxième album pour avancer.`,
  editor_stuck:
    `Bonjour {firstName}, votre album {destination} est presque prêt — il reste juste à cliquer sur Terminer. {link}`,
  awaiting_preview_feedback:
    `Bonjour {firstName}, vous avez pu jeter un œil à l'aperçu 3D sur ce lien ? {link}`,
  changes_requested:
    `On a bien fait les modifications demandées. Dites-moi si c'est tout bon sur ce lien {link}.`,
  submitted_waiting:
    `Bonjour {firstName}, merci ! Votre album est bien reçu, on s'en occupe et on revient vers vous très vite.`,
  awaiting_payment:
    `Bonjour {firstName}, on lance l'impression dès réception de l'avance de {acompte} MAD. Voici le RIB : {rib}`,
  submitted:
    `Bonjour {firstName} 😊\n\nVos albums {destinations} sont bien reçus — merci pour votre confiance ! 🎉\n\nPour lancer l'impression, il nous faut une petite avance de votre part :\n\n🏦 Titulaire : OMAR IHAHANE\n💳 RIB : 230 330 3719980211005800 16\n💰 Petite avance (minimum) : {acompte} MAD\n✨ Ou paiement complet : {total} MAD\n\nEnvoyez-nous la photo du reçu et on s'occupe de tout 🙏\n\nL'équipe Storiz`,
  printed_not_shipped:
    `Bonjour {firstName}, votre album est imprimé. La livraison est organisée très prochainement.`,
  in_transit:
    `Bonjour {firstName}, votre album est en route, vous le recevrez bientôt.`,
  delivered:
    `Bonjour {firstName}, votre album est livré. On espère qu'il vous plaît — un avis nous ferait vraiment plaisir.`,
};

// ─── 3D-preview conversion pitch (2026-06-10) ────────────────────────────────
// For customers who uploaded photos but stalled — never clicked ✨ Magic IA
// (`mode_not_chosen`), never opened the generated album (`editor_not_opened`),
// or got stuck mid-editor (`editor_stuck`) — the admin can build their album
// for them (editor regen → PDFs → preview assets → preview link) and the
// relance flips from "come back and do the work" to "your album is ALREADY
// made — come look at it". Showing the finished 3D book is the strongest
// conversion lever we have: zero effort left for the client, pure emotion.
export const PREVIEW_PITCH_SUB_STATES: ReadonlySet<SubState> = new Set<SubState>([
  'editor_stuck',
]);

/** Friendly, clean, high-conversion copy. `{destination}` renders with its own
 *  leading space from the caller (empty destination = clause disappears). The
 *  preview link sits on its own line for the WhatsApp link preview. */
export const DEFAULT_PREVIEW_PITCH_TEMPLATE =
  `Bonjour {firstName} 🌸\n\nPetite surprise : votre album{destination} est déjà prêt ! On l'a préparé avec vos photos et il est magnifique 🤩\n\nFeuilletez-le en 3D ici 👇\n{previewLink}\n\nIl vous plaît ? Répondez simplement « OK » et on lance l'impression 📖💛\nUne retouche ? Dites-nous tout, on s'en occupe.\n\nL'équipe Storiz`;

/** Render the 3D-preview conversion pitch. Admin can override the copy via
 *  app_settings.follow_up_templates['preview_pitch'] (same store as the
 *  per-sub-state templates — renderMessage's customTemplates param). */
export function renderPreviewPitch(
  vars: { firstName: string; destination: string; previewLink: string },
  customTemplates: Record<string, string> | null,
): string {
  const tpl = customTemplates?.['preview_pitch'] ?? DEFAULT_PREVIEW_PITCH_TEMPLATE;
  const destClause = vars.destination.trim() ? ` ${vars.destination.trim()}` : '';
  return tpl
    .replaceAll('{firstName}', vars.firstName.trim() || 'cher client')
    .replaceAll('{destination}', destClause)
    .replaceAll('{previewLink}', vars.previewLink);
}

/** Render a template with the given vars. Custom (admin-edited) templates
 *  win over the bundled `DEFAULT_TEMPLATES`. Final fallback is the original
 *  factory-function output. */
export function renderMessage(
  subState: SubState,
  vars: MessageVars,
  customTemplates: Record<string, string> | null,
): string {
  const tpl = customTemplates?.[subState] ?? DEFAULT_TEMPLATES[subState];
  if (tpl) {
    return tpl
      .replaceAll('{firstName}', vars.firstName.trim() || 'cher client')
      .replaceAll('{destinations}', (vars.destinationsList ?? vars.destination ?? '').trim())
      .replaceAll('{destination}', vars.destination.trim())
      .replaceAll('{link}', vars.link)
      .replaceAll('{acompte}', vars.acompte != null ? String(vars.acompte) : '')
      .replaceAll('{total}', vars.total != null ? String(vars.total) : '')
      .replaceAll('{rib}', (vars.rib ?? '').trim() || '230 330 3719980211005800 16');
  }
  // Should be unreachable now that DEFAULT_TEMPLATES covers every SubState,
  // but keep the original factory function as a defensive fallback.
  return FOLLOW_UP_MESSAGES[subState](vars);
}

/** Short human-readable French label for the sub-state — used in the admin
 *  UI badge + bulk list column. */
export const SUB_STATE_LABELS: Record<SubState, string> = {
  not_started:               'Rien commencé',
  photos_missing:            'Photos manquantes',
  cover_not_chosen:          'Couverture non choisie',
  album_2_missing:           'Album suivant manquant',
  editor_stuck:              'Bloqué dans l\'éditeur',
  awaiting_preview_feedback: 'Aperçu envoyé — sans réponse',
  changes_requested:         'Modifications demandées (à faire)',
  awaiting_payment:          'Validé — en attente de paiement',
  submitted:                 'Album soumis',
  submitted_waiting:         'Album soumis — rien à demander au client',
  printed_not_shipped:       'En production / Imprimé',
  in_transit:                'Expédié',
  delivered:                 'Livré',
};
